import React, { useMemo } from "react";
import { TableRow, TableCell, IconButton, TextField, Box, Typography } from "@mui/material";
import { FastField } from "formik";
import DeleteIcon from "../../Images/delete.png";
import { Dropdown } from "../../HelperFunctions/PayrollDropdown";
import { TABLE_STYLES, renderCoaOption } from "./wageTableUtils";
import LocalTextField from "./LocalTextField";
import { parseHours, formatHours } from "./wageTableUtils";
import { ITEM_TYPE } from "../../Enums/Enum";

/**
 * A single wage row in the mapping table.
 *
 * Props (3 grouped objects):
 *   rowData   — { row, index, isRemoving, touchedRow, errorRow }
 *   ctx       — { showItemColumn, isReadOnly, isHoursDecimal, isType1, formValuesDepartment,
 *                 formValuesJob, normalizedOptions, normalizedOptionsMap, accountOptionsMap, accountOptions }
 *   handlers  — { setFieldValue, enqueueSnackbar, handleCOA, onAddNewMappedItem,
 *                 remove, setRemovingIndex, showDeleteConfirm, setOpenAddCOA, setPendingCOAField }
 */
const WageRow = React.memo(function WageRow(props) {
    const rowData = props.rowData || {
        row: props.row,
        index: props.index,
        isRemoving: props.isRemoving,
        touchedRow: props.rowTouched || props.touchedRow,
        errorRow: props.rowError || props.errorRow,
    };
    const ctx = props.ctx || {
        showItemColumn: props.showItemColumn,
        isReadOnly: props.isReadOnly,
        isHoursDecimal: props.isHoursDecimal ?? props.values?.IsHoursDecimal,
        isType1: props.isType1,
        formValuesDepartment: props.formValuesDepartment,
        formValuesJob: props.formValuesJob,
        normalizedOptions: props.normalizedOptions,
        normalizedOptionsMap: props.normalizedOptionsMap,
        accountOptionsMap: props.accountOptionsMap,
        accountOptions: props.accountOptions,
    };
    const handlers = props.handlers || {
        setFieldValue: props.setFieldValue,
        enqueueSnackbar: props.enqueueSnackbar,
        handleCOA: props.handleCOA,
        onAddNewMappedItem: props.onAddNewMappedItem,
        remove: props.remove,
        setRemovingIndex: props.setRemovingIndex,
        showDeleteConfirm: props.showDeleteConfirm,
        setOpenAddCOA: props.setOpenAddCOA,
        setPendingCOAField: props.setPendingCOAField,
    };

    const { row, index, isRemoving, touchedRow, errorRow } = rowData;
    const {
        showItemColumn, isReadOnly, isHoursDecimal, isType1,
        formValuesDepartment, formValuesJob,
        normalizedOptions, normalizedOptionsMap, accountOptionsMap, accountOptions,
    } = ctx;
    const {
        setFieldValue, enqueueSnackbar, handleCOA, onAddNewMappedItem,
        remove, setRemovingIndex, showDeleteConfirm, setOpenAddCOA, setPendingCOAField,
    } = handlers;


    const selectedOption = useMemo(() =>
        normalizedOptionsMap.get(String(row.mappedTo).toLowerCase()) ||
        { label: row.mappedTo ? (row.mappedToName || row.mappedTo || "") : "--Select--", value: row.mappedTo || "" },
        [row.mappedTo, row.mappedToName, normalizedOptionsMap]
    );

    const selectedCOAOption = useMemo(() => {
        const coaKey = String(row.coa || "");
        return coaKey
            ? (accountOptionsMap.get(coaKey) || accountOptionsMap.get(coaKey.toLowerCase()) || accountOptionsMap.get(coaKey.toUpperCase()) || { label: row.coaName || coaKey, value: row.coa })
            : (accountOptionsMap.get("") || { label: "--Select--", value: "" });
    }, [row.coa, row.coaName, accountOptionsMap]);

    const rowWageOptions = useMemo(() => {
        const opts = normalizedOptions || [];
        if (selectedOption?.value && !normalizedOptionsMap.has(String(selectedOption.value).toLowerCase())) {
            return [...opts, selectedOption];
        }
        return opts;
    }, [normalizedOptions, selectedOption, normalizedOptionsMap]);

    const rowAccountOptions = useMemo(() => {
        const opts = accountOptions || [];
        if (selectedCOAOption?.value && !accountOptionsMap.has(String(selectedCOAOption.value).toLowerCase())) {
            return [...opts, selectedCOAOption];
        }
        return opts;
    }, [accountOptions, selectedCOAOption, accountOptionsMap]);

    const formattedHrs = useMemo(() => {
        const rawHrs = row.hrs ?? "";
        if (isHoursDecimal && rawHrs !== "") {
            if (String(rawHrs).includes(':')) {
                const [h, m] = String(rawHrs).split(':');
                const decimal = (parseInt(h, 10) || 0) + ((parseInt(m, 10) || 0) / 60);
                return decimal.toFixed(2);
            }
            return Number(rawHrs).toFixed(2);
        }
        return rawHrs;
    }, [row.hrs, isHoursDecimal]);

    return (
        <TableRow sx={{ backgroundColor: "#ffffff", opacity: isRemoving ? 0 : 1, transform: isRemoving ? "translateY(-5px)" : "translateY(0)", transition: "all 0.2s ease" }}>
            {showItemColumn && (
                <TableCell sx={{ height: '35px', boxSizing: 'border-box', p: '5px' }}>
                    <FastField name={`wage[${index}].wageName`}>
                        {({ field }) => (
                            <TextField disabled {...field} size="small" title={field.value}
                                sx={{ ...TABLE_STYLES.input, "& .MuiInputBase-input": { textAlign: "left !important" }, "& .MuiInputBase-input.Mui-disabled": { textAlign: "left !important", WebkitTextFillColor: "#000 !important", color: "#000 !important" } }}
                            />
                        )}
                    </FastField>
                </TableCell>
            )}

            {/* Mapped To */}
            <TableCell sx={{ height: '35px', boxSizing: 'border-box', p: '5px' }}>
                <Dropdown
                    sx={TABLE_STYLES.dropdown}
                    name={`wage[${index}].mappedTo`}
                    disabled={isReadOnly}
                    options={rowWageOptions}
                    value={selectedOption}
                    error={touchedRow?.mappedTo && Boolean(errorRow?.mappedTo)}
                    getOptionLabel={(option) => option?.label || ""}
                    isEqual={(option, val) => String(option?.value || "").toLowerCase() === String(val?.value || "").toLowerCase()}
                    onChange={(name, newValue) => {
                        if (newValue?.value === "ADD_NEW") {
                            if (!formValuesDepartment) { enqueueSnackbar("Please select the Department ", { variant: "error" }); return; }
                            if (!formValuesJob) { enqueueSnackbar("Please select the Job Title", { variant: "error" }); return; }
                            if (onAddNewMappedItem) onAddNewMappedItem(ITEM_TYPE.Wages, row.wageName || "", index, "wage");
                            return;
                        }
                        const selectedVal = newValue?.value || "";
                        setFieldValue(`wage[${index}]`, { ...row, mappedTo: selectedVal, mappedToName: newValue?.label || "" });
                        if (selectedVal) handleCOA(newValue, index);
                        else setFieldValue(`wage[${index}].coa`, "");
                    }}
                    renderOption={(props, option) => {
                        const { key, ...restProps } = props;
                        const label = option.label || "";
                        if (option.value === "ADD_NEW") return (
                            <li key={key} {...restProps} title={label} style={{ color: "#d32f2f", fontWeight: 500 }}>
                                <span title={label}>{label}</span>
                            </li>
                        );
                        return (
                            <li key={key} {...restProps} title={label}>
                                <Box display="flex" alignItems="center" width="100%" minWidth={0}>
                                    <Typography fontSize={11} sx={{ flex: 1, whiteSpace: "nowrap" }}>
                                        <span title={label}>{label}</span>
                                    </Typography>
                                </Box>
                            </li>
                        );
                    }}
                    popperSx={{ width: "fit-content", minWidth: 150 }}
                />
            </TableCell>

            {/* COA */}
            <TableCell sx={{ height: '35px', boxSizing: 'border-box', p: '5px' }}>
                <Dropdown
                    name={`wage[${index}].coa`}
                    disabled={isReadOnly}
                    options={rowAccountOptions}
                    value={selectedCOAOption}
                    error={touchedRow?.coa && Boolean(errorRow?.coa)}
                    getOptionLabel={(option) => option?.label || ""}
                    isEqual={(option, val) => String(option?.value || option?.ID || "").toLowerCase() === String(val?.value || val?.ID || "").toLowerCase()}
                    onChange={(name, newValue) => {
                        if (newValue?.value === "ADD_NEW") {
                            if (setPendingCOAField && setOpenAddCOA) { setPendingCOAField(`wage[${index}].coa`); setOpenAddCOA(true); }
                            return;
                        }
                        setFieldValue(`wage[${index}]`, { ...row, coa: newValue?.value || "", coaName: newValue?.label || "" });
                    }}
                    renderOption={renderCoaOption}
                    sx={{ minWidth: 175, "& .MuiFilledInput-root": { height: "28px", minHeight: "28px", fontSize: "12px", bgcolor: "white", padding: "0px !important", "&.Mui-disabled": { backgroundColor: "#f5f5f5 !important" } }, "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000 !important", color: "#000 !important" }, "& .MuiFilledInput-input": { padding: "0 5px !important" } }}
                    popperSx={{ width: "400px !important", maxWidth: "calc(100vw - 24px)" }}
                />
            </TableCell>

            {/* Hours */}
            <TableCell sx={{ height: '35px', boxSizing: 'border-box', p: '5px' }}>
                <LocalTextField
                    sx={TABLE_STYLES.input} size="small" disabled={isReadOnly} value={formattedHrs}
                    onValueChange={(val) => {
                        const regex = isHoursDecimal ? /^\d*\.?\d*$/ : /^\d*[.:]?\d*$/;
                        if (!regex.test(val)) { enqueueSnackbar("Only up to 2 decimal places are allowed.", { variant: "error" }); return false; }
                        if (isHoursDecimal) {
                            if (!/^\d*\.?\d{0,2}$/.test(val)) { enqueueSnackbar("Only up to 2 decimal places are allowed.", { variant: "error" }); return false; }
                        } else {
                            if (!/^\d*[.:]?\d{0,2}$/.test(val)) {
                                if (val.includes('.')) enqueueSnackbar("Only up to 2 decimal places are allowed.", { variant: "error" });
                                else enqueueSnackbar("Minutes cannot exceed 2 digits.", { variant: "error" });
                                return false;
                            }
                            if (val.includes(':')) {
                                const parts = val.split(':');
                                if (parts[1] && parts[1].length > 0 && parseInt(parts[1], 10) > 59) { enqueueSnackbar("Minutes cannot be greater than 59.", { variant: "error" }); return false; }
                            }
                        }
                        return true;
                    }}
                    onValueBlur={(value) => {
                        let hrsDec = 0; let newHrs = "0.00";
                        if (isHoursDecimal) {
                            let val = parseFloat(value);
                            if (isNaN(val) || String(value).trim() === '' || String(value).trim() === '-') val = 0;
                            hrsDec = Number(val.toFixed(2)); newHrs = hrsDec.toFixed(2);
                        } else {
                            hrsDec = value ? parseHours(value, false) : 0; newHrs = formatHours(hrsDec);
                        }
                        const rate = Number(row.rate || 0); const gross = Number(row.gross || 0);
                        let newGross = row.gross; let newRate = row.rate;
                        if (!showItemColumn) {
                            if (hrsDec > 0) {
                                if (rate > 0) newGross = (hrsDec * rate).toFixed(2);
                                else if (gross > 0) newRate = (gross / hrsDec).toFixed(2);
                            }
                        }
                        setFieldValue(`wage[${index}]`, { ...row, hrs: newHrs, gross: newGross, rate: newRate });
                    }}
                    inputProps={{ style: { textAlign: "left" } }}
                    clearOnFocusZero={true}
                />
            </TableCell>

            {/* Rate */}
            <TableCell sx={{ height: '35px', boxSizing: 'border-box', p: '5px' }}>
                <LocalTextField
                    sx={TABLE_STYLES.input} size="small" disabled={isReadOnly} value={row.rate ?? ""}
                    onValueChange={(val) => {
                        if (/^[0-9,]*\.?[0-9]{0,2}$/.test(val)) return true;
                        if (val !== "" && val !== "-") enqueueSnackbar("Only up to 2 decimal places are allowed.", { variant: "error" });
                        return false;
                    }}
                    onValueBlur={(value) => {
                        const cleanValue = String(value).replace(/,/g, '');
                        let val = parseFloat(cleanValue);
                        if (isNaN(val) || String(cleanValue).trim() === '' || String(cleanValue).trim() === '-') val = 0;
                        const hrs = parseHours(row.hrs, isHoursDecimal);
                        let newGross = row.gross;
                        if (isType1 && hrs > 0) newGross = (hrs * val).toFixed(2); // eslint-disable-line
                        setFieldValue(`wage[${index}]`, { ...row, rate: val.toFixed(2), gross: newGross });
                    }}
                    clearOnFocusZero={true}
                    inputProps={{ style: { textAlign: "left" } }}
                />
            </TableCell>

            {/* Gross Pay */}
            <TableCell sx={{ height: '35px', boxSizing: 'border-box', p: '5px' }}>
                <LocalTextField
                    sx={TABLE_STYLES.input} size="small" disabled={isReadOnly}
                    value={Number(row.gross || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    clearOnFocusZero={true}
                    error={touchedRow?.gross && Boolean(errorRow?.gross)}
                    onValueChange={(val) => {
                        if (/^-?[0-9,]*\.?[0-9]{0,2}$/.test(val)) return true;
                        if (val !== "" && val !== "-") enqueueSnackbar("Only numbers with up to 2 decimal places are allowed.", { variant: "error" });
                        return false;
                    }}
                    onValueBlur={(value) => {
                        const cleanValue = String(value).replace(/,/g, '');
                        let val = parseFloat(cleanValue);
                        if (isNaN(val) || String(cleanValue).trim() === '' || String(cleanValue).trim() === '-') val = 0;
                        const hrs = parseHours(row.hrs, isHoursDecimal);
                        let newRate = row.rate;
                        if (!showItemColumn && hrs > 0) newRate = (val / hrs).toFixed(2);
                        setFieldValue(`wage[${index}]`, { ...row, gross: val.toFixed(2), rate: newRate });
                    }}
                    inputProps={{ style: { textAlign: "right" } }}
                />
            </TableCell>

            {/* Delete */}
            <TableCell align="center" sx={{ height: '35px', boxSizing: 'border-box', p: '5px' }}>
                <IconButton disabled={isReadOnly} onClick={() => {
                    const hasData = Boolean(row.gross && Number(row.gross) !== 0);
                    const performDelete = () => { setRemovingIndex(index); setTimeout(() => { remove(index); setRemovingIndex(null); }, 200); };
                    if (hasData) showDeleteConfirm("Are you sure you want to delete the row?", performDelete);
                    else performDelete();
                }}>
                    <img src={DeleteIcon} alt="Delete" height={18} width={18} />
                </IconButton>
            </TableCell>
        </TableRow>
    );
}, (prev, next) => {
    return (
        prev.rowData === next.rowData &&
        prev.ctx === next.ctx &&
        prev.handlers === next.handlers &&
        prev.row === next.row &&
        prev.index === next.index &&
        prev.isRemoving === next.isRemoving
    );
});


export default WageRow;
